import type { ResolvedEditAreaDefinition } from '@repo/api-contracts';
import type { CreateContext, MeasureText, RenderMaterials } from '@repo/design-renderer';
import type { Raster } from '@repo/warp-engine';
import type { ReactElement } from 'react';
import {
  type BoxNode,
  type FlatFitMode,
  faceSurfacesOf,
  layersOf,
  useEditorStore,
} from '@/features/design-editor';
import { CanvasNote } from './canvas-note';
import { FacePhotoOnly } from './face-photo-only';
import { FlatEditCanvas } from './flat-edit-canvas';

interface DesignCanvasProps {
  readonly definition: ResolvedEditAreaDefinition;
  readonly materials: RenderMaterials;
  readonly guide: Raster | null;
  readonly createContext: CreateContext;
  readonly measure: MeasureText;
  readonly onChangeNode: (node: BoxNode) => void;
  readonly onNudge: (dxMm: number, dyMm: number) => void;
  readonly onDeleteSelected: () => void;
  readonly onEditText: (nodeId: string) => void;
  readonly onBackdropError: () => void;
  /** 곡면 상품의 실제 합성(손을 멈추면 뒤에서 그린다). 평면 상품은 null. */
  readonly warped: ImageBitmap | null;
  readonly onWarpedHit: (photoPx: { x: number; y: number }) => string | null;
  /** 보기 모드(전체 보기/영역 확대) — 스테이지의 토글이 정하고, 캔버스가 휠로 바꾸면 알린다. */
  readonly fitMode: FlatFitMode;
  readonly onFitModeChange: (mode: FlatFitMode) => void;
}

/**
 * 목업 위 바로 편집 — 지금 색상·면의 목업 사진 위에 그 면의 인쇄 영역 전부를 제자리에 겹친다(마플식).
 * 편집↔미리보기 전환이 없어 선택을 풀어도 화면이 그대로다. 영역이 여럿이어도 탭 없이 한 화면에서 누르는 대로 편집한다.
 */
export const DesignCanvas = ({
  definition,
  materials,
  guide,
  createContext,
  measure,
  onChangeNode,
  onNudge,
  onDeleteSelected,
  onEditText,
  onBackdropError,
  warped,
  onWarpedHit,
  fitMode,
  onFitModeChange,
}: DesignCanvasProps): ReactElement => {
  const { document, faceKey, componentId, selectedNodeId, setComponent, select, clearSelection } =
    useEditorStore();
  const faceSurfaces = faceSurfacesOf({
    definition,
    colorOptionId: document.colorOptionId,
    faceKey,
  });
  const surfaces = faceSurfaces.slots.map((slot) => ({
    ...slot,
    layers: layersOf(document, slot.component.id),
  }));
  const activeComponentId =
    surfaces.find((surface) => surface.component.id === componentId)?.component.id ??
    surfaces[0]?.component.id ??
    '';

  return (
    <div className="relative size-full">
      <div className="absolute inset-0">
        {surfaces.length === 0 ? (
          <FacePhotoOnly url={faceSurfaces.backdrop?.url ?? null} />
        ) : (
          <FlatEditCanvas
            surfaces={surfaces}
            space={faceSurfaces.space}
            activeComponentId={activeComponentId}
            onActivate={setComponent}
            selectedNodeId={selectedNodeId}
            materials={materials}
            guide={guide}
            createContext={createContext}
            measure={measure}
            backdrop={faceSurfaces.backdrop}
            onSelect={(id) => (id === null ? clearSelection() : select(id))}
            onChangeNode={onChangeNode}
            onNudge={onNudge}
            onDeleteSelected={onDeleteSelected}
            onEditText={onEditText}
            onBackdropError={onBackdropError}
            warped={warped}
            onWarpedHit={onWarpedHit}
            fitMode={fitMode}
            onFitModeChange={onFitModeChange}
          />
        )}
      </div>
      {faceSurfaces.backdrop?.approximate === true && (
        <CanvasNote>곡면 상품은 움직이는 동안 평면으로, 손을 떼면 곡면으로 보여요</CanvasNote>
      )}
    </div>
  );
};
